/**
 * Pure Milestones helpers — grouping, next-tier display, template
 * rendering/validation, and client-side definition validation.
 *
 * No React Native / network imports: this module is shared by components AND
 * the Node-side Playwright state spec (transformed via Babel like the guides
 * harness), so it must stay dependency-free. SQL/the catalog RPC remains
 * authoritative; these helpers only shape presentation and pre-validation.
 */
import { MILESTONE_PRESENTATION, MILESTONE_SCOPES } from '@/config/milestones';

/** Matches allowlisted placeholders such as {{threshold}} or {{personFirstName}}. */
const PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*\}\}/g;

/** Placeholder names available to every scope (fallback if catalog omits). */
export const COMMON_PLACEHOLDERS = ['threshold', 'metricValue', 'milestoneName', 'organizationName'];
export const INDIVIDUAL_ONLY_PLACEHOLDERS = ['personFirstName'];

/**
 * Extracts the distinct placeholder names used in a template.
 * @param {string} template
 * @returns {string[]}
 */
export const extractTemplatePlaceholders = (template) => {
    if (typeof template !== 'string' || !template) return [];
    const found = new Set();
    for (const match of template.matchAll(PLACEHOLDER_PATTERN)) {
        found.add(match[1]);
    }
    return [...found];
};

/**
 * Returns the allowlist of placeholder names for a scope.
 * @param {string} scope - 'ORGANIZATION' | 'INDIVIDUAL'
 * @param {object} [catalog] - get_milestone_catalog() payload (authoritative).
 * @returns {string[]}
 */
export const allowedPlaceholdersForScope = (scope, catalog) => {
    const common = catalog?.placeholders?.common ?? COMMON_PLACEHOLDERS;
    const individualOnly = catalog?.placeholders?.individualOnly ?? INDIVIDUAL_ONLY_PLACEHOLDERS;
    return scope === MILESTONE_SCOPES.INDIVIDUAL ? [...common, ...individualOnly] : [...common];
};

/**
 * Validates a celebration template against the placeholder allowlist.
 * Unknown placeholders are validation errors — the server enforces the same.
 * @param {string} template
 * @param {string[]} allowed - allowed placeholder names.
 * @returns {{ valid: boolean, unknown: string[] }}
 */
export const validateMilestoneTemplate = (template, allowed) => {
    const unknown = extractTemplatePlaceholders(template).filter(name => !allowed.includes(name));
    return { valid: unknown.length === 0, unknown };
};

/**
 * Renders a template by substituting allowlisted placeholders. Unknown or
 * missing values keep their literal token so the author sees what is wrong.
 * @param {string} template
 * @param {object} values - { threshold, metricValue, milestoneName, organizationName, personFirstName }
 * @param {string[]} [allowed] - when provided, only these placeholders render.
 * @returns {string}
 */
export const renderMilestoneTemplate = (template, values = {}, allowed = null) => {
    if (typeof template !== 'string') return '';
    return template.replace(PLACEHOLDER_PATTERN, (token, name) => {
        if (allowed && !allowed.includes(name)) return token;
        const value = values?.[name];
        return value === undefined || value === null ? token : String(value);
    });
};

/**
 * Sorts threshold entries ascending by value (defensive against bigint-safe
 * string serialization from the RPC layer).
 * @param {{id: string|null, threshold: number|string}[]} thresholds
 * @returns {array}
 */
export const sortMilestoneThresholds = (thresholds = []) =>
    [...(thresholds || [])].sort((a, b) => Number(a.threshold) - Number(b.threshold));

/**
 * The next unearned tier at/above the current metric value, for progress UI.
 * @param {array} thresholds - series thresholds.
 * @param {number|string} currentValue - current authoritative metric value.
 * @returns {object|null} the lowest threshold above currentValue, else null.
 */
export const nextMilestoneThreshold = (thresholds = [], currentValue = 0) =>
    sortMilestoneThresholds(thresholds).find(t => Number(t.threshold) > Number(currentValue)) ?? null;

/**
 * Groups person/org awards by series for the badges tab, sorting each series'
 * awards by threshold and flagging the highest earned tier per series.
 * Accepts the grouped `series` payload from get_person_milestone_state /
 * get_organization_milestone_history (or a flat award list as a fallback).
 * @param {object[]|object} state - { series: [...] } or a MilestoneAward[] list.
 * @returns {Array<{
 *   milestoneID: string, name: string, enabled: boolean, deletedAt: string|null,
 *   badgeIcon: string, badgeColorToken: string, awards: object[],
 *   highestThreshold: number|null, latestEarnedAt: string|null,
 *   progress: object|null, usesTrackingStart: boolean
 * }>}
 */
export const groupMilestonesBySeries = (state) => {
    const source = Array.isArray(state) ? { series: [{ awards: state }] } : (state ?? {});
    const series = source.series ?? source.groups ?? [];

    const groups = series.map(entry => {
        const awards = sortMilestoneThresholds(
            (entry.awards ?? entry.badges ?? []).map(award => ({
                ...award,
                id: award.id ?? award.awardID ?? null,
                threshold: award.threshold ?? award.thresholdValue,
                earnedAt: award.earnedAt ?? award.recordedAt ?? null,
                badgeIcon: award.badgeIcon ?? award.snapshot?.badgeIcon ?? null,
                badgeColorToken: award.badgeColorToken ?? award.snapshot?.badgeColorToken ?? null,
            }))
        );
        return {
            milestoneID: entry.milestoneID ?? entry.id ?? null,
            scope: entry.scope,
            name: entry.name ?? entry.milestoneName ?? 'Milestone',
            metricKey: entry.metricKey,
            enabled: entry.enabled !== false,
            deletedAt: entry.deletedAt ?? (entry.archived ? 'archived' : null),
            badgeIcon: entry.badgeIcon ?? awards[0]?.badgeIcon ?? MILESTONE_PRESENTATION.defaultBadgeIcon,
            badgeColorToken: entry.badgeColorToken ?? awards[0]?.badgeColorToken ?? MILESTONE_PRESENTATION.defaultBadgeColorToken,
            awards,
            thresholds: sortMilestoneThresholds(entry.thresholds ?? []),
            highestThreshold: awards.length ? Number(awards[awards.length - 1].threshold) : null,
            latestEarnedAt: awards.reduce((latest, award) => (award.earnedAt && (!latest || award.earnedAt > latest) ? award.earnedAt : latest), null),
            progress: entry.progress ?? null,
            // Days Active counts only from trackingStartedAt. Prefer the
            // explicit flag; fall back to the well-known metric key.
            usesTrackingStart: entry.usesTrackingStart === true || entry.metricKey === 'reach_active_days',
        };
    });

    // Order by latest earned activity; series without awards (progress-only)
    // keep their configured order at the end.
    return groups.sort((a, b) => {
        if (a.latestEarnedAt && b.latestEarnedAt) return a.latestEarnedAt < b.latestEarnedAt ? 1 : -1;
        if (a.latestEarnedAt) return -1;
        if (b.latestEarnedAt) return 1;
        return 0;
    });
};

/**
 * Folds the flat `awards` + `progress` payload from
 * get_organization_milestone_history into grouped `series` entries matching
 * the shape get_person_milestone_state already returns (grouped per series).
 * Server has already applied the financial gate — no re-filtering here.
 * @param {object} payload - { awards: [], progress: [] }
 * @returns {object[]} series entries for groupMilestonesBySeries.
 */
export const milestoneHistoryToSeries = (payload) => {
    const awards = payload?.awards ?? [];
    const progress = payload?.progress ?? [];
    const bySeries = new Map();

    for (const award of awards) {
        const key = award?.milestoneID;
        if (!key) continue;
        const entry = bySeries.get(key) ?? {
            milestoneID: key,
            name: award.name,
            metricKey: award.metricKey,
            scope: award.scope,
            enabled: award.enabled !== false,
            archived: award.archived === true,
            badgeIcon: award.badgeIcon ?? award.snapshot?.badgeIcon ?? null,
            badgeColorToken: award.badgeColorToken ?? award.snapshot?.badgeColorToken ?? null,
            awards: [],
            progress: null,
        };
        entry.awards.push(award);
        bySeries.set(key, entry);
    }

    for (const prog of progress) {
        const key = prog?.milestoneID;
        if (!key) continue;
        const entry = bySeries.get(key) ?? {
            milestoneID: key,
            name: prog.name,
            metricKey: prog.metricKey,
            scope: 'ORGANIZATION',
            enabled: true,
            archived: false,
            badgeIcon: null,
            badgeColorToken: null,
            awards: [],
            progress: null,
        };
        entry.name = entry.name ?? prog.name;
        entry.metricKey = entry.metricKey ?? prog.metricKey;
        entry.progress = prog;
        bySeries.set(key, entry);
    }

    return [...bySeries.values()];
};

/**
 * Accessible label for a badge, e.g. "Total Donations, 50 donations badge".
 * @param {object} params - { seriesName, threshold, earned }
 * @returns {string}
 */
export const milestoneBadgeLabel = ({ seriesName, threshold, earned = true }) =>
    `${seriesName ?? 'Milestone'} milestone${threshold != null ? `, ${threshold}` : ''}${earned ? ' completed' : ''}`;

/**
 * Validates editor state against catalog bounds. Returns a flat errors map;
 * the server re-enforces every rule — this is UX pre-validation only.
 * @param {object} state - editor state { scope, metricKey, filters, thresholds, name, title, message, badgeIcon, badgeColorToken, includeAdministrators, roleIDs }.
 * @param {object} catalog - get_milestone_catalog() payload.
 * @returns {{ valid: boolean, errors: Record<string, string> }}
 */
export const validateMilestoneDefinition = (state, catalog) => {
    const errors = {};
    const bounds = catalog?.thresholdBounds ?? MILESTONE_PRESENTATION.fallbackThresholdBounds;
    const text = catalog?.textBounds ?? MILESTONE_PRESENTATION.fallbackTextBounds;
    const scope = state?.scope;
    const metric = catalog?.metrics?.find(m => m.key === state?.metricKey);

    if (!scope || !Object.values(MILESTONE_SCOPES).includes(scope)) {
        errors.scope = 'Choose who this milestone applies to.';
    }
    if (!state?.metricKey) {
        errors.metricKey = 'Choose what counts toward this milestone.';
    } else if (catalog?.metrics && !metric) {
        errors.metricKey = 'That metric is not in the milestone catalog.';
    } else if (metric && scope && !(metric.scopes ?? []).includes(scope)) {
        errors.metricKey = `${metric.label} is not available for ${scope === MILESTONE_SCOPES.ORGANIZATION ? 'organization' : 'individual'} milestones.`;
    }

    // Filters: required filters must hold a selection (exactly one for
    // non-multiple required filters like Small Group Type); explicitly empty
    // lists are rejected to avoid accidental broadening.
    for (const filter of metric?.filters ?? []) {
        const selected = state?.filters?.[filter.key];
        const count = Array.isArray(selected) ? selected.length : (selected ? 1 : 0);
        if (filter.required && count === 0) {
            errors[`filters.${filter.key}`] = `${filter.label} is required for this metric.`;
        } else if (filter.required && !filter.multiple && count !== 1) {
            errors[`filters.${filter.key}`] = `Choose exactly one ${filter.label}.`;
        } else if (Array.isArray(selected) && selected.length === 0 && count === 0 && !filter.required) {
            // Omitted (undefined/null) means "all" — an explicitly empty list is invalid.
            if (filter.key in (state?.filters ?? {})) {
                errors[`filters.${filter.key}`] = `Remove ${filter.label} or select at least one option.`;
            }
        }
        if (catalog?.maxFilterIDs && count > catalog.maxFilterIDs) {
            errors[`filters.${filter.key}`] = `Too many ${filter.label} selected (max ${catalog.maxFilterIDs}).`;
        }
    }

    const name = (state?.name ?? '').trim();
    const title = state?.title ?? '';
    const message = state?.message ?? '';
    if (!name) errors.name = 'Name this milestone.';
    else if (text.name?.min != null && name.length < text.name.min) errors.name = `Name must be at least ${text.name.min} characters.`;
    else if (text.name?.max != null && name.length > text.name.max) errors.name = `Name must be ${text.name.max} characters or fewer.`;
    if (text.title?.max != null && title.length > text.title.max) errors.title = `Title must be ${text.title.max} characters or fewer.`;
    if (text.message?.max != null && message.length > text.message.max) errors.message = `Message must be ${text.message.max} characters or fewer.`;

    const allowed = allowedPlaceholdersForScope(scope, catalog);
    for (const [field, value] of [['title', title], ['message', message]]) {
        const { valid, unknown } = validateMilestoneTemplate(value, allowed);
        if (!valid) errors[field] = `Unknown placeholder${unknown.length > 1 ? 's' : ''}: ${unknown.map(n => `{{${n}}}`).join(', ')}.`;
    }

    const thresholds = sortMilestoneThresholds(state?.thresholds ?? []).map(t => Number(t.threshold));
    if (thresholds.length === 0) {
        errors.thresholds = 'Add at least one threshold.';
    } else {
        if (bounds.min != null && thresholds.length < bounds.min) errors.thresholds = `Add at least ${bounds.min} threshold${bounds.min === 1 ? '' : 's'}.`;
        if (bounds.max != null && thresholds.length > bounds.max) errors.thresholds = `No more than ${bounds.max} thresholds per milestone.`;
        if (thresholds.some(t => !Number.isSafeInteger(t) || t < (bounds.minValue ?? 1) || (bounds.maxValue != null && t > bounds.maxValue))) {
            errors.thresholds = `Thresholds must be whole numbers between ${bounds.minValue ?? 1} and ${bounds.maxValue ?? 'the supported maximum'}.`;
        }
        if (new Set(thresholds).size !== thresholds.length) errors.thresholds = 'Thresholds must be unique.';
    }

    if (state?.badgeIcon && catalog?.badgeIcons?.length && !catalog.badgeIcons.includes(state.badgeIcon)) {
        errors.badgeIcon = 'Choose a badge icon from the list.';
    }
    if (state?.badgeColorToken && catalog?.badgeColorTokens?.length && !catalog.badgeColorTokens.includes(state.badgeColorToken)) {
        errors.badgeColorToken = 'Choose a badge color from the list.';
    }

    // Individual milestones have no milestone-specific role eligibility.
    if (scope === MILESTONE_SCOPES.INDIVIDUAL && (state?.includeAdministrators || (state?.roleIDs ?? []).length > 0)) {
        errors.audience = 'Individual milestones do not have an audience setting.';
    }

    return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Builds the save_milestone definition payload from editor state.
 * @param {object} state - editor state (see validateMilestoneDefinition).
 * @param {object} [existing] - existing definition when editing (for id).
 * @returns {import('../types/Milestone').MilestoneSavePayload}
 */
export const buildMilestoneSavePayload = (state, existing = null) => {
    const filters = {};
    for (const [key, ids] of Object.entries(state?.filters ?? {})) {
        if (Array.isArray(ids) && ids.length > 0) filters[key] = ids;
    }
    return {
        id: existing?.id ?? state?.id ?? null,
        scope: state.scope,
        name: (state.name ?? '').trim(),
        title: state.title ?? '',
        message: state.message ?? '',
        metricKey: state.metricKey,
        filters,
        thresholds: sortMilestoneThresholds(state.thresholds ?? []).map(t => ({ id: t.id ?? null, threshold: Number(t.threshold) })),
        badgeIcon: state.badgeIcon ?? MILESTONE_PRESENTATION.defaultBadgeIcon,
        badgeColorToken: state.badgeColorToken ?? MILESTONE_PRESENTATION.defaultBadgeColorToken,
        includeAdministrators: state.scope === MILESTONE_SCOPES.ORGANIZATION ? state.includeAdministrators === true : false,
        roleIDs: state.scope === MILESTONE_SCOPES.ORGANIZATION ? (state.roleIDs ?? []) : [],
        enabled: state.enabled === true,
    };
};

/**
 * Builds the payload for duplicating an awarded series: new ID, new tier IDs,
 * forced disabled — a genuinely new badge identity, not a trigger reset.
 * @param {object} definition
 * @returns {import('../types/Milestone').MilestoneSavePayload}
 */
export const buildMilestoneDuplicatePayload = (definition) => ({
    id: null,
    scope: definition.scope,
    name: `${definition.name} (copy)`.slice(0, 120),
    title: definition.title,
    message: definition.message,
    metricKey: definition.metricKey,
    filters: definition.filters ?? {},
    thresholds: sortMilestoneThresholds(definition.thresholds ?? []).map(t => ({ id: null, threshold: Number(t.threshold) })),
    badgeIcon: definition.badgeIcon,
    badgeColorToken: definition.badgeColorToken,
    includeAdministrators: definition.includeAdministrators === true,
    roleIDs: definition.roleIDs ?? [],
    enabled: false,
});

/* -------------------------------------------------------------------------
 * Phase E — celebration presentation helpers.
 * Pure functions only (no RN/network imports) so the Playwright state spec
 * can exercise the provider's lease/gating decisions through Babel.
 * ---------------------------------------------------------------------- */

/**
 * Normalizes one claimed celebration item (or a replay snapshot) into the
 * entry shape MilestoneCelebrationModal renders. The frozen claim contract
 * already flattens snapshot fields; replay returns them under `snapshot`.
 * @param {object} item - claim item or replay payload.
 * @returns {{receiptID: string|null, awardID: string|null, milestoneID: string|null,
 *   scope: string|null, name: string, title: string, message: string,
 *   threshold: number|null, badgeIcon: string, badgeColorToken: string}}
 */
export const normalizeCelebrationItem = (item) => {
    const snapshot = item?.snapshot && typeof item.snapshot === 'object' ? item.snapshot : {};
    return {
        receiptID: item?.receiptID ?? null,
        awardID: item?.awardID ?? item?.id ?? null,
        milestoneID: item?.milestoneID ?? null,
        scope: item?.scope ?? null,
        name: item?.name ?? item?.milestoneName ?? snapshot.name ?? 'Milestone',
        title: item?.title ?? snapshot.title ?? MILESTONE_PRESENTATION.defaultTitle,
        message: item?.message ?? item?.renderedMessage ?? snapshot.message ?? '',
        threshold: item?.threshold != null ? Number(item.threshold) : null,
        badgeIcon: item?.badgeIcon ?? item?.icon ?? snapshot.badgeIcon ?? MILESTONE_PRESENTATION.defaultBadgeIcon,
        badgeColorToken: item?.badgeColorToken ?? item?.color ?? snapshot.badgeColorToken ?? MILESTONE_PRESENTATION.defaultBadgeColorToken,
    };
};

/**
 * Normalizes a claim payload's items (or a bare item array) for presentation.
 * @param {object|array} claim - claim_own_milestone_celebrations payload or items array.
 * @returns {array}
 */
export const normalizeCelebrationItems = (claim) =>
    (Array.isArray(claim) ? claim : claim?.items ?? []).map(normalizeCelebrationItem);

/**
 * Normalizes a get_milestone_award_replay payload into a presentation entry.
 * @param {object} replay
 * @returns {object|null}
 */
export const normalizeAwardReplay = (replay) =>
    replay ? normalizeCelebrationItem(replay) : null;

/**
 * Lease guard: expired leases are never presented (a re-claimed lease returns
 * to pending automatically). Tolerates ISO strings and epoch numbers.
 * @param {string|number|null} leaseExpiresAt
 * @param {number} [now] - Date.now() epoch ms.
 * @returns {boolean}
 */
export const isMilestoneLeaseExpired = (leaseExpiresAt, now = Date.now()) => {
    if (leaseExpiresAt == null) return false;
    const expiry = typeof leaseExpiresAt === 'number' ? leaseExpiresAt : Date.parse(leaseExpiresAt);
    return Number.isFinite(expiry) && expiry <= now;
};

/**
 * The single gate deciding whether the automatic celebration provider may
 * claim/present right now. Returns a stable reason string when blocked, or
 * null when the Dashboard is a safe, idle, focused presentation surface.
 *
 * Mirrors §11.3: automatic presentation is Dashboard-only, requires a
 * hydrated authed context, an online foregrounded app, and no competing
 * surfaces (guide tour, loader, confirmation, dropdown, sheet blockers,
 * dashboard-local modals, or a focused text input).
 *
 * @param {object} signals
 * @param {boolean} signals.enabled - release flag.
 * @param {boolean} signals.authenticated - account/org/person all resolved.
 * @param {boolean} signals.ready - dashboard data loaded.
 * @param {boolean} signals.focused - dashboard screen focused.
 * @param {boolean} signals.foreground - AppState active / visible web tab.
 * @param {boolean} signals.online - NetInfo connected (null/undefined = online).
 * @param {boolean} [signals.keyboardOpen]
 * @param {boolean} [signals.inputFocused] - a text input currently has focus.
 * @param {boolean} [signals.loaderOpen]
 * @param {boolean} [signals.confirmationOpen]
 * @param {boolean} [signals.menuOpen] - dropdown menu open.
 * @param {number}  [signals.blockerCount] - registered guide blockers (sheets/modals) minus the celebration's own.
 * @param {boolean} [signals.guideActive] - a contextual guide is active/starting.
 * @param {boolean} [signals.presentationReserved] - another presenter holds the reservation.
 * @param {boolean} [signals.emulating]
 * @param {boolean} [signals.kiosk]
 * @param {boolean} [signals.localBlocked] - dashboard-local surface open (settings/shortcuts/reaction).
 * @returns {string|null}
 */
export const celebrationBlockedReason = ({
    enabled = true,
    authenticated = true,
    ready = true,
    focused = true,
    foreground = true,
    online = true,
    keyboardOpen = false,
    inputFocused = false,
    loaderOpen = false,
    confirmationOpen = false,
    menuOpen = false,
    blockerCount = 0,
    guideActive = false,
    presentationReserved = false,
    emulating = false,
    kiosk = false,
    localBlocked = false,
} = {}) => {
    if (!enabled) return 'flag-off';
    if (!authenticated) return 'unauthenticated';
    if (kiosk) return 'kiosk';
    if (emulating) return 'emulation';
    if (!ready) return 'not-ready';
    if (!focused) return 'unfocused';
    if (!foreground) return 'background';
    if (online === false) return 'offline';
    // Guides win the idle-Dashboard handshake — a tour that is starting or
    // active defers the celebration to the next safe batch.
    if (guideActive) return 'guide-active';
    if (presentationReserved) return 'presentation-reserved';
    if (loaderOpen) return 'loader';
    if (confirmationOpen) return 'confirmation';
    if (menuOpen) return 'menu';
    if (localBlocked) return 'dashboard-surface';
    if (blockerCount > 0) return 'surface';
    if (keyboardOpen || inputFocused) return 'input';
    return null;
};

/**
 * Picks the "View Achievements" destination for a dismissed batch
 * (§11.4): individual-only batches go to the viewer's own profile Badges
 * tab; organization-only batches go to the organization Milestones page —
 * but only for viewers authorized to read org history (never route an
 * audience-only recipient into a management screen). Mixed batches return
 * null (Continue only).
 * @param {array} items - normalized celebration entries.
 * @param {object} params - { personID, canViewOrgHistory }
 * @returns {string|null}
 */
export const celebrationBatchRoute = (items, { personID, canViewOrgHistory } = {}) => {
    const list = items ?? [];
    if (!list.length) return null;
    const allIndividual = list.every(item => item.scope === MILESTONE_SCOPES.INDIVIDUAL);
    const allOrganization = list.every(item => item.scope === MILESTONE_SCOPES.ORGANIZATION);
    if (allIndividual) return personID ? `/people/${personID}?tab=badges` : null;
    if (allOrganization && canViewOrgHistory) return '/settings/organization/milestones';
    return null;
};

/**
 * Active-day throttle: record at most once per local device day per
 * account/org (server dedup is authoritative; this only avoids the RPC).
 * @param {object|null} saved - { attemptedDeviceDate } previously persisted.
 * @param {string} todayDeviceDate - today's local ISO date (yyyy-MM-dd).
 * @returns {boolean}
 */
export const shouldRecordActiveDay = (saved, todayDeviceDate) =>
    !!todayDeviceDate && saved?.attemptedDeviceDate !== todayDeviceDate;

/**
 * Claim-payload guard: a usable lease needs an id and at least one item.
 * Empty claims (cross-device lease loss, opt-out mid-flight) are no-ops.
 * @param {object|null} claim
 * @returns {boolean}
 */
export const isUsableCelebrationClaim = (claim) =>
    !!claim?.leaseID && Array.isArray(claim?.items) && claim.items.length > 0;
